/**
 * Demo journey-view override — TEST ACCOUNT ONLY.
 *
 * The dashboard normally decides "new user (early journey)" vs "established
 * (full access)" purely from real activity: `strengthMap.total_sessions <= 1`.
 * That is exactly the behaviour every real account keeps.
 *
 * The hardcoded demo account (test@gmail.com) is pinned to a frozen demo
 * dataset, so completing real scenarios can't move its journey — which is why
 * it looked "stuck on first scenario". To demo BOTH states on demand, the For
 * Testing panel writes a view preference here; the dashboard reads it ONLY for
 * the test account and forces the matching view. No other account ever reads
 * this value, so all other accounts behave as usual.
 */

export type DemoJourneyView = 'new' | 'full' | null;

const KEY = 'lanura_demo_journey_view';
export const DEMO_JOURNEY_EVENT = 'lanura:demo-journey';

export function getDemoJourneyView(): DemoJourneyView {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'new' || v === 'full' ? v : null;
  } catch {
    return null;
  }
}

export function setDemoJourneyView(view: DemoJourneyView): void {
  try {
    if (view === null) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, view);
  } catch {
    /* storage unavailable — non-blocking */
  }
  // Let an already-open dashboard react without a full reload.
  try {
    window.dispatchEvent(new CustomEvent(DEMO_JOURNEY_EVENT, { detail: view }));
  } catch {
    /* no-op */
  }
}

/** The single hardcoded demo identity — centralised so the dashboard, the
 *  Settings "For Testing" panel and the floating toggle all agree. */
export function isMainTestAccountEmail(email?: string | null): boolean {
  return (
    String(email || '')
      .toLowerCase()
      .trim() === 'test@gmail.com'
  );
}

/** The journey-preview options, shared by the Settings panel and the floating
 *  dashboard toggle so the two can never drift. */
export const JOURNEY_VIEWS: Array<{ id: DemoJourneyView; label: string; sub: string }> = [
  { id: 'new', label: 'New user', sub: 'Early journey - "start your first practice"' },
  { id: 'full', label: 'In journey', sub: 'Full access - progress, scores & pathways unlocked' },
  { id: null, label: 'Real data', sub: 'Behaves like a normal account (your real sessions)' },
];

export function journeyViewMessage(view: DemoJourneyView): string {
  return view === 'new'
    ? 'Previewing the new-user journey'
    : view === 'full'
      ? 'Previewing full access (in journey)'
      : 'Using your real data';
}

/**
 * Core of the journey-view switch, shared by the Settings panel and the
 * floating dashboard toggle:
 *   1. flip the localStorage view override (+ DEMO_JOURNEY_EVENT) so the
 *      early-journey vs return-visit view re-renders instantly;
 *   2. reseed/disable the matching demo dataset. Each testingAPI call runs
 *      bustDemoSurfaces() internally, invalidating the dashboard / pathways /
 *      history queries so charts + data refetch LIVE with no page reload.
 * testingAPI is imported dynamically to keep this widely-imported util's
 * static graph free of the API client. Returns the API response (chartDemo).
 */
export async function applyJourneyView(view: DemoJourneyView): Promise<any> {
  setDemoJourneyView(view);
  const { testingAPI } = await import('../services/api');
  const fn =
    view === 'new'
      ? testingAPI.resetDemoCharts
      : view === 'full'
        ? testingAPI.seedDemoCharts
        : testingAPI.disableDemoCharts;
  return fn();
}
